import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  useCameraStream,
  useVideoFile,
  usePoseDetection,
  useVideoRecorder,
  drawPoseHud,
} from '@/domains/pose';
import { PoseSourceTabs } from './components/PoseSourceTabs/PoseSourceTabs';
import { VideoUploader } from './components/VideoUploader/VideoUploader';
import { PoseStage } from './components/PoseStage/PoseStage';
import { PoseControls } from './components/PoseControls/PoseControls';
import { PoseMediaTray } from './components/PoseMediaTray/PoseMediaTray';
import { PoseGuide } from './components/PoseGuide/PoseGuide';
import './PoseStudio.css';

export function PoseStudio() {
  const [sourceMode, setSourceMode] = useState('camera');
  const [autoClipEnabled, setAutoClipEnabled] = useState(false);
  const [autoClipStatus, setAutoClipStatus] = useState('IDLE'); // 'IDLE' | 'RECORDING' | 'SAVED' | 'ABORTED'
  const [shotCount, setShotCount] = useState(0);
  const [dominantHand, setDominantHand] = useState('right'); // 'right' (기본값: 우측 기준) | 'left'
  const [isMirrored, setIsMirrored] = useState(true); // 기본값: 거울 반전 ON
  const [mediaList, setMediaList] = useState([]);

  const prevPhaseRef = useRef('SETUP');
  const shotAttemptActiveRef = useRef(false);
  const hasReleasedRef = useRef(false);
  const statusResetTimerRef = useRef(null);

  const camera = useCameraStream();
  const file = useVideoFile();
  const recorder = useVideoRecorder();

  const isDetecting =
    sourceMode === 'camera' ? camera.isStreaming : Boolean(file.videoUrl);

  const isPaused =
    sourceMode === 'camera' ? camera.isPaused : !file.isPlaying;

  const {
    canvasRef,
    isLoadingModel,
    modelError,
    fps,
    hasPoseDetected,
    angles,
    phaseData,
    clearCanvas,
  } = usePoseDetection({
    videoRef: camera.videoRef,
    isStreaming: isDetecting,
    isPaused: sourceMode === 'camera' ? camera.isPaused : false,
    dominantHand,
  });

  const hudDataRef = useRef({});
  hudDataRef.current = {
    angles,
    phaseData,
    fps,
    dominantHand,
    isPaused,
    autoClipEnabled,
    autoClipStatus,
    shotCount,
  };

  const handleAddMediaItem = useCallback((item) => {
    setMediaList((prev) => [item, ...prev]);
  }, []);

  const handleDeleteItems = useCallback((idsToDelete) => {
    setMediaList((prev) => {
      const set = new Set(idsToDelete);
      prev.forEach((item) => {
        if (set.has(item.id) && item.url) {
          URL.revokeObjectURL(item.url);
        }
      });
      return prev.filter((item) => !set.has(item.id));
    });
  }, []);

  const handleClearAllMedia = useCallback(() => {
    setMediaList((prev) => {
      prev.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
      return [];
    });
  }, []);

  // AI 사격 단계 감지 기반 자동 컷팅 녹화 제어
  useEffect(() => {
    if (!autoClipEnabled) {
      if (shotAttemptActiveRef.current) {
        recorder.cancelRecording();
        shotAttemptActiveRef.current = false;
        hasReleasedRef.current = false;
      }
      return;
    }

    const currentPhase = phaseData?.phase || 'SETUP';
    const prevPhase = prevPhaseRef.current;

    // 1. SETUP -> DRAW: 사격 시위 당김 감지 시 자동 녹화 시작
    if (prevPhase === 'SETUP' && currentPhase === 'DRAW') {
      if (camera.videoRef.current && canvasRef.current && !shotAttemptActiveRef.current) {
        shotAttemptActiveRef.current = true;
        hasReleasedRef.current = false;
        if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);

        recorder.startRecording({
          videoElement: camera.videoRef.current,
          canvasElement: canvasRef.current,
          filenamePrefix: `aimus-shot-${shotCount + 1}`,
          autoDownload: false,
          isMirrored,
          getHudData: () => hudDataRef.current,
          onRecordComplete: (recordedItem) => {
            handleAddMediaItem(recordedItem);
          },
        });
        setAutoClipStatus('RECORDING');
      }
    }

    // 2. 발사(RELEASE) 단계 감지: 발사 완료 플래그 기록 (녹화는 유지하여 팔로우스루 포함)
    if (currentPhase === 'RELEASE' && shotAttemptActiveRef.current) {
      hasReleasedRef.current = true;
    }

    // 3. 준비(SETUP) 단계로 전환될 때 녹화 마무리 판정
    if (currentPhase === 'SETUP' && shotAttemptActiveRef.current) {
      // (A) 발사 단계를 거친 후 준비 단계로 복귀한 경우: 영상 정상 저장 및 카운트 증가
      if (hasReleasedRef.current || prevPhase === 'RELEASE') {
        recorder.stopRecording();
        shotAttemptActiveRef.current = false;
        hasReleasedRef.current = false;
        setShotCount((prev) => prev + 1);
        setAutoClipStatus('SAVED');

        if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
        statusResetTimerRef.current = setTimeout(() => {
          setAutoClipStatus('IDLE');
        }, 2500);
      }
      // (B) 발사 없이 중간에 준비 단계로 복귀한 경우: 영상 폐기/삭제
      else {
        recorder.cancelRecording();
        shotAttemptActiveRef.current = false;
        hasReleasedRef.current = false;
        setAutoClipStatus('ABORTED');

        if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
        statusResetTimerRef.current = setTimeout(() => {
          setAutoClipStatus('IDLE');
        }, 2500);
      }
    }

    prevPhaseRef.current = currentPhase;
  }, [phaseData?.phase, autoClipEnabled, shotCount, camera.videoRef, canvasRef, recorder, handleAddMediaItem]);

  // 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    };
  }, []);

  const handleToggleAutoClip = () => {
    setAutoClipEnabled((prev) => {
      const next = !prev;
      if (!next) {
        if (shotAttemptActiveRef.current) {
          recorder.cancelRecording();
          shotAttemptActiveRef.current = false;
          hasReleasedRef.current = false;
        }
        setAutoClipStatus('IDLE');
      }
      return next;
    });
  };

  const handleSelectMode = (mode) => {
    if (shotAttemptActiveRef.current) {
      recorder.cancelRecording();
      shotAttemptActiveRef.current = false;
      hasReleasedRef.current = false;
    } else if (recorder.isRecording) {
      recorder.stopRecording();
    }
    setAutoClipStatus('IDLE');
    clearCanvas();
    if (mode === 'file' && camera.isStreaming) camera.stopCamera();
    setSourceMode(mode);
  };

  const handleStopCamera = () => {
    if (shotAttemptActiveRef.current) {
      recorder.cancelRecording();
      shotAttemptActiveRef.current = false;
      hasReleasedRef.current = false;
    } else if (recorder.isRecording) {
      recorder.stopRecording();
    }
    setAutoClipStatus('IDLE');
    clearCanvas();
    camera.stopCamera();
  };

  const handleTogglePlayVideo = () => {
    if (!camera.videoRef.current) return;
    if (file.isPlaying) {
      camera.videoRef.current.pause();
      file.setIsPlaying(false);
    } else {
      camera.videoRef.current.play();
      file.setIsPlaying(true);
    }
  };

  const handleToggleRecordVideo = () => {
    if (recorder.isRecording) {
      recorder.stopRecording();
      if (sourceMode === 'file' && file.isPlaying && camera.videoRef.current) {
        camera.videoRef.current.pause();
        file.setIsPlaying(false);
      }
    } else {
      if (!camera.videoRef.current || !canvasRef.current) return;
      if (sourceMode === 'file') {
        if (!file.isPlaying) {
          camera.videoRef.current.play();
          file.setIsPlaying(true);
        }
        recorder.startRecording({
          videoElement: camera.videoRef.current,
          canvasElement: canvasRef.current,
          filenamePrefix: 'aimus-video-analysis',
          autoDownload: false,
          isMirrored,
          getHudData: () => hudDataRef.current,
          onRecordComplete: (recordedItem) => {
            handleAddMediaItem(recordedItem);
          },
        });
      } else {
        recorder.startRecording({
          videoElement: camera.videoRef.current,
          canvasElement: canvasRef.current,
          filenamePrefix: 'aimus-webcam-analysis',
          autoDownload: false,
          isMirrored,
          getHudData: () => hudDataRef.current,
          onRecordComplete: (recordedItem) => {
            handleAddMediaItem(recordedItem);
          },
        });
      }
    }
  };

  const handleVideoEnded = () => {
    file.setIsPlaying(false);
    if (shotAttemptActiveRef.current) {
      recorder.cancelRecording();
      shotAttemptActiveRef.current = false;
      hasReleasedRef.current = false;
      setAutoClipStatus('IDLE');
    } else if (recorder.isRecording) {
      recorder.stopRecording();
    }
  };

  const handleSnapshot = () => {
    if (!camera.videoRef.current || !canvasRef.current) return;
    const width = camera.videoRef.current.videoWidth || canvasRef.current.width || 1280;
    const height = camera.videoRef.current.videoHeight || canvasRef.current.height || 720;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const ctx = tempCanvas.getContext('2d');

    if (isMirrored) {
      ctx.save();
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(camera.videoRef.current, 0, 0, width, height);
      ctx.drawImage(canvasRef.current, 0, 0, width, height);
      ctx.restore();
    } else {
      ctx.drawImage(camera.videoRef.current, 0, 0, width, height);
      ctx.drawImage(canvasRef.current, 0, 0, width, height);
    }

    // 태블릿 기준 자세 분석 HUD(각도/타이머/페이즈 박스) 캔버스 합성
    drawPoseHud(ctx, {
      width,
      height,
      ...hudDataRef.current,
    });

    // 워터마크 브랜딩
    ctx.save();
    ctx.fillStyle = 'rgba(15, 23, 42, 0.6)';
    ctx.fillRect(width - 165, height - 36, 155, 26);
    ctx.fillStyle = '#38bdf8';
    ctx.font = 'bold 12px Outfit, sans-serif';
    ctx.fillText('AIMUS AI VISION', width - 153, height - 19);
    ctx.restore();

    tempCanvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const filename = `aimus-posture-${Date.now()}.png`;

      handleAddMediaItem({
        id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: 'image',
        url,
        blob,
        name: filename,
        createdAt: new Date(),
      });
    }, 'image/png');
  };

  return (
    <section className="pose-studio">
      <div className="pose-studio__header">
        <span className="pose-studio__badge">LIVE AI VISION</span>
        <h2 className="pose-studio__title">AI 실시간 양궁 자세 분석 스튜디오</h2>
        <p className="pose-studio__desc">
          웹캠 실시간 촬영 또는 저장된 영상을 불러와 33개 관절 뼈대를 정밀 분석합니다.
        </p>
      </div>

      <div className="pose-studio__content">
        <PoseSourceTabs sourceMode={sourceMode} onSelectMode={handleSelectMode} />

        <PoseGuide />

        {sourceMode === 'file' && (
          <VideoUploader
            videoFile={file.videoFile}
            onFileSelect={file.loadFile}
            onClear={file.clearVideo}
            error={file.fileError}
          />
        )}

        <PoseStage
          videoRef={camera.videoRef}
          canvasRef={canvasRef}
          isStreaming={isDetecting}
          videoUrl={file.videoUrl}
          sourceMode={sourceMode}
          fps={fps}
          hasPoseDetected={hasPoseDetected}
          isLoadingModel={isLoadingModel}
          angles={angles}
          phaseData={phaseData}
          isRecording={recorder.isRecording}
          formattedDuration={recorder.formattedDuration}
          isConverting={recorder.isConverting}
          conversionProgress={recorder.conversionProgress}
          isPaused={isPaused}
          autoClipEnabled={autoClipEnabled}
          autoClipStatus={autoClipStatus}
          shotCount={shotCount}
          dominantHand={dominantHand}
          isMirrored={isMirrored}
          onToggleDominantHand={() => setDominantHand((prev) => (prev === 'right' ? 'left' : 'right'))}
          cameraError={camera.cameraError || recorder.recorderError}
          modelError={modelError}
          onVideoEnded={handleVideoEnded}
        />

        <PoseControls
          sourceMode={sourceMode}
          isStreaming={camera.isStreaming}
          hasVideoUrl={Boolean(file.videoUrl)}
          isPlayingVideo={file.isPlaying}
          isPausedCamera={camera.isPaused}
          autoClipEnabled={autoClipEnabled}
          onStartCamera={camera.startCamera}
          onStopCamera={handleStopCamera}
          onTogglePauseCamera={camera.togglePauseCamera}
          onTogglePlayVideo={handleTogglePlayVideo}
          onToggleAutoClip={handleToggleAutoClip}
          onSnapshot={handleSnapshot}
          isLoadingModel={isLoadingModel}
        />

        <PoseMediaTray
          mediaList={mediaList}
          onDeleteItems={handleDeleteItems}
          onClearAll={handleClearAllMedia}
        />
      </div>
    </section>
  );
}
